// src/utils/dashboardDataService.js

const isInRange = (dateStr, startDateStr, endDateStr) => {
	if (!startDateStr && !endDateStr) return true;
	if (startDateStr && dateStr < startDateStr) return false;
	if (endDateStr && dateStr > endDateStr) return false;
	return true;
};

const filterDomainLevelData = (analyticsRows, revenueRows, filters = {}) => {
	const { startDate, endDate, domain } = filters;
	const s = startDate || null;
	const e = endDate || null;

	const domainMatch = (rowDomain) =>
		!domain || domain === "all" || rowDomain === domain;

	const a = (analyticsRows || []).filter(
		(row) => isInRange(row.Date, s, e) && domainMatch(row.Domain)
	);

	const r = (revenueRows || []).filter(
		(row) => isInRange(row.Date, s, e) && domainMatch(row.Domain)
	);

	return { analytics: a, revenue: r };
};

const secondsToHHMMSS = (totalSeconds) => {
	if (!totalSeconds || totalSeconds <= 0) return "00:00:00";
	const secs = Math.floor(totalSeconds);
	const h = String(Math.floor(secs / 3600)).padStart(2, "0");
	const m = String(Math.floor((secs % 3600) / 60)).padStart(2, "0");
	const s = String(secs % 60).padStart(2, "0");
	return `${h}:${m}:${s}`;
};

export const getRevenueSummaryData = (
	analyticsRows,
	revenueRows,
	filters = {}
) => {
	console.log(
		"getRevenueSummaryData param",
		analyticsRows,
		revenueRows,
		filters
	);
	const { analytics, revenue } = filterDomainLevelData(
		analyticsRows,
		revenueRows,
		filters
	);

	let totalInventory = 0;
	let totalRevenue = 0;

	analytics.forEach((row) => {
		totalInventory += Number(row.InventoryCreated || 0);
	});

	revenue.forEach((row) => {
		totalRevenue += Number(row.Total_Revenue || 0);
	});

	const rpm = totalInventory > 0 ? (totalRevenue / totalInventory) * 1000 : 0;

	return {
		inventoryCount: totalInventory,
		totalRevenue: Number(totalRevenue.toFixed(2)),
		rpm: Number(rpm.toFixed(2))
	};
};

export const getRevenueByDateChartData = (
	analyticsRows,
	revenueRows,
	filters = {}
) => {
	const { analytics, revenue } = filterDomainLevelData(
		analyticsRows,
		revenueRows,
		filters
	);

	const byDate = {};

	analytics.forEach((row) => {
		const d = row.Date;
		if (!byDate[d]) byDate[d] = { date: d, inventory: 0, revenue: 0 };
		byDate[d].inventory += row.InventoryCreated || 0;
	});

	revenue.forEach((row) => {
		const d = row.Date;
		if (!byDate[d]) byDate[d] = { date: d, inventory: 0, revenue: 0 };
		byDate[d].revenue += row.Total_Revenue || 0;
	});

	const result = Object.values(byDate).map((item) => {
		const inv = item.inventory || 0;
		const rev = item.revenue || 0;
		return {
			date: item.date,
			revenue: Number(rev.toFixed(2)),
			rpm: inv > 0 ? Number(((rev / inv) * 1000).toFixed(2)) : 0
		};
	});

	result.sort((a, b) => a.date.localeCompare(b.date));
	return result;
};

export const getSolutionRevenueChartData = (
	analyticsRows,
	revenueRows,
	filters = {}
) => {
	const { analytics, revenue } = filterDomainLevelData(
		analyticsRows,
		revenueRows,
		filters
	);

	const solutionDefs = [
		{ key: "StoryIQ", label: "Story IQ" },
		{ key: "PulseIQ", label: "Pulse IQ" },
		{ key: "ImageIQ", label: "Image IQ" },
		{ key: "VideoIQ", label: "Video IQ" }
	];

	return solutionDefs.map(({ key, label }) => {
		const revKey = `${key}_Revenue`;
		const loadKey = `${key}_Loaded`;

		let solutionRevenue = 0;
		let solutionInventory = 0;

		revenue.forEach((row) => {
			solutionRevenue += row[revKey] || 0;
		});

		analytics.forEach((row) => {
			solutionInventory += row[loadKey] || 0;
		});

		const solutionRpm =
			solutionInventory > 0
				? Number(((solutionRevenue / solutionInventory) * 1000).toFixed(2))
				: 0;

		return {
			solution: label,
			revenue: Number(solutionRevenue.toFixed(2)),
			rpm: solutionRpm
		};
	});
};

export const getTopDomainsRevenueChartData = (
	analyticsRows,
	revenueRows,
	filters = {},
	maxItems = 3
) => {
	const { analytics, revenue } = filterDomainLevelData(
		analyticsRows,
		revenueRows,
		filters
	);

	// InventoryCreated per domain (analytics)
	const invByDomain = {};
	(analytics || []).forEach((row) => {
		const d = row.Domain;
		invByDomain[d] = (invByDomain[d] || 0) + Number(row.InventoryCreated || 0);
	});

	// Revenue per domain (SUM duplicates)
	const revByDomain = {};
	(revenue || []).forEach((row) => {
		const d = row.Domain;
		revByDomain[d] = (revByDomain[d] || 0) + Number(row.Total_Revenue || 0);
	});

	// Final chart data (one row per domain)
	const arr = Object.keys(revByDomain).map((domain) => {
		const rev = revByDomain[domain] || 0;
		const inv = invByDomain[domain] || 0;

		return {
			domain,
			revenue: Number(rev.toFixed(2)),
			rpm: inv > 0 ? Number(((rev / inv) * 1000).toFixed(2)) : 0
		};
	});

	// Sort by revenue desc
	arr.sort((a, b) => b.revenue - a.revenue);
	return arr.slice(0, maxItems);
};

export const getTopCountriesRevenueChartData = (
	analyticsCountriesRows,
	revenueCountriesRows,
	maxItems = 3
) => {
	// InventoryCreated per country (analytics)
	const invByCountry = {};
	(analyticsCountriesRows || []).forEach((row) => {
		const c = row.Country;
		invByCountry[c] =
			(invByCountry[c] || 0) + Number(row.InventoryCreated || 0);
	});

	const arr = (revenueCountriesRows || [])
		.map((row) => {
			const country = row.Country;
			const rawRevenue = Number(row.Total_Revenue || 0);
			const revenue = Number(rawRevenue.toFixed(2)); // ✅ rounded once
			const inventory = Number(invByCountry[country] || 0);

			return {
				country,
				revenue,
				inventory,
				rpm:
					inventory > 0 && revenue > 0
						? Number(((revenue / inventory) * 1000).toFixed(2))
						: 0
			};
		})
		// ✅ REMOVE zero-revenue countries
		.filter((item) => item.revenue > 0);

	// Sort & limit
	arr.sort((a, b) => b.revenue - a.revenue);
	return arr.slice(0, maxItems);
};

export const getAnalyticsByDateChartData = (analyticsRows, filters = {}) => {
	const { analytics } = filterDomainLevelData(analyticsRows, [], filters);

	const byDate = {};

	analytics.forEach((row) => {
		const d = row.Date;
		if (!byDate[d])
			byDate[d] = {
				date: d,
				engSeconds: 0,
				articleSeconds: 0
			};

		byDate[d].engSeconds += row.Total_EngagementTime || 0;
		byDate[d].articleSeconds += row.Article_EngagementTime || 0;
	});

	let arr = Object.values(byDate).map((item) => {
		const hours = item.engSeconds / 3600;
		const boost =
			item.articleSeconds > 0
				? (item.engSeconds / item.articleSeconds) * 100
				: 0;

		return {
			date: item.date,
			engagementHours: Number(hours.toFixed(1)),
			boostPercent: Number(boost.toFixed(1))
		};
	});

	arr.sort((a, b) => a.date.localeCompare(b.date));
	return arr;
};

export const getSolutionAnalyticsChartData = (analyticsRows, filters = {}) => {
	const { analytics } = filterDomainLevelData(analyticsRows, [], filters);

	const solutionDefs = [
		{ key: "StoryIQ_EngagementTime", label: "StoryIQ" },
		{ key: "PulseIQ_EngagementTime", label: "PulseIQ" },
		{ key: "VideoIQ_EngagementTime", label: "VideoIQ" },
		{ key: "ImageIQ_EngagementTime", label: "ImageIQ" }
	];

	let totalArticleEng = 0;
	analytics.forEach((row) => {
		totalArticleEng += row.Article_EngagementTime || 0;
	});

	return solutionDefs.map(({ key, label }) => {
		let sec = 0;
		analytics.forEach((row) => {
			sec += row[key] || 0;
		});

		const hours = sec / 3600;
		const boost = totalArticleEng > 0 ? (sec / totalArticleEng) * 100 : 0;

		return {
			solution: label,
			engagementHours: Number(hours.toFixed(1)),
			boostPercent: Number(boost.toFixed(1))
		};
	});
};

export const getTopDomainsAnalyticsChartData = (
	analyticsRows,
	filters = {},
	maxItems = 3
) => {
	const { analytics } = filterDomainLevelData(analyticsRows, [], filters);

	const domainMap = {};

	analytics.forEach((row) => {
		const d = row.Domain;
		if (!domainMap[d])
			domainMap[d] = {
				domain: d,
				engSeconds: 0,
				articleSeconds: 0
			};

		domainMap[d].engSeconds += row.Total_EngagementTime || 0;
		domainMap[d].articleSeconds += row.Article_EngagementTime || 0;
	});

	let arr = Object.values(domainMap).map((item) => {
		const hours = item.engSeconds / 3600;
		const boost =
			item.articleSeconds > 0
				? (item.engSeconds / item.articleSeconds) * 100
				: 0;

		return {
			domain: item.domain,
			engagementHours: Number(hours.toFixed(1)),
			boostPercent: Number(boost.toFixed(1))
		};
	});

	arr.sort((a, b) => b.engagementHours - a.engagementHours);
	return arr.slice(0, maxItems);
};

export const getTopCountriesAnalyticsChartData = (
	analyticsCountriesRows,
	filters = {},
	maxItems = 3
) => {
	const { analytics } = filterDomainLevelData(
		analyticsCountriesRows,
		[],
		filters
	);

	const countryMap = {};

	(analytics || []).forEach((row) => {
		const c = row.Country || "UNKNOWN";
		if (!countryMap[c]) {
			countryMap[c] = { country: c, engSeconds: 0, articleSeconds: 0 };
		}

		countryMap[c].engSeconds += Number(row.Total_EngagementTime || 0);
		countryMap[c].articleSeconds += Number(row.Article_EngagementTime || 0);
	});

	const arr = Object.values(countryMap)
		.map((item) => {
			const engagementHours = Number((item.engSeconds / 3600).toFixed(2));
			const boost =
				item.articleSeconds > 0
					? (item.engSeconds / item.articleSeconds) * 100
					: 0;

			return {
				country: item.country,
				engagementHours,
				boostPercent: Number(boost.toFixed(1))
			};
		})
		// ✅ FILTER: remove items that are still 0 after rounding
		.filter((item) => item.engagementHours > 0)
		// ✅ SORT: highest engagement first
		.sort((a, b) => b.engagementHours - a.engagementHours)
		// ✅ LIMIT
		.slice(0, maxItems);

	return arr;
};

export const getAnalyticsSummaryData = (analyticsRows, filters = {}) => {
	const { analytics } = filterDomainLevelData(analyticsRows, [], filters);

	let totalEngagementTime = 0;
	let totalArticleEngagementTime = 0;
	let totalInventoryCount = 0;
	let totalClicks = 0;

	analytics.forEach((row) => {
		totalEngagementTime += Number(row.Total_EngagementTime || 0);
		totalArticleEngagementTime += Number(row.Article_EngagementTime || 0);
		totalInventoryCount += Number(row.InventoryCreated || 0);
		totalClicks += Number(row.IQClicks || 0);
	});

	const ctr =
		totalInventoryCount > 0 ? (totalClicks / totalInventoryCount) * 100 : 0;

	const engagementBoost =
		totalArticleEngagementTime > 0
			? (totalEngagementTime / totalArticleEngagementTime) * 100
			: 0;

	return {
		engagementTimeHHMMSS: secondsToHHMMSS(totalEngagementTime),
		ctr,
		engagementBoost
	};
};
